import React from "react";
import { Box, SimpleGrid, Text, Badge, Wrap, WrapItem, Flex, useColorModeValue } from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import Card from "components/card/Card.js";
import { GOLD_TEXT, GOLD_LIGHT } from "constants/brandColors";

function RankedList({ items, renderLabel, t }) {
  const rowHoverBg = useColorModeValue("gray.50", "whiteAlpha.100");
  if (!items?.length) {
    return <Text color="gray.500" textAlign="center" py={6}>{t("No data found")}</Text>;
  }
  return (
    <Box>
      {items.map((item, index) => (
        <Flex
          key={item.categoryId ?? item.userId ?? index}
          justify="space-between"
          align="center"
          px={3}
          py={2}
          borderRadius="md"
          _hover={{ bg: rowHoverBg }}
        >
          <Text fontSize="sm">
            <Text as="span" color="gray.400" me={2}>{index + 1}.</Text>
            {renderLabel(item) || t("Unknown")}
          </Text>
          <Badge bg={GOLD_LIGHT} color={GOLD_TEXT}>{item.count}</Badge>
        </Flex>
      ))}
    </Box>
  );
}

// `overview` is /reports/content-overview's data: all-time (not date-ranged)
// { byStatus: {status:count}, byCategory: [{categoryId,slug,name,count}],
// byAuthor: [{userId,name,count}] } — "what does the catalog look like right
// now" rather than a trend, so this panel deliberately ignores the page's
// date-range filter.
export default function ContentOverviewPanel({ overview }) {
  const { t } = useTranslation();
  const byStatusEntries = Object.entries(overview?.byStatus || {});

  return (
    <Card w="100%" p="20px">
      <Text color={GOLD_TEXT} fontSize="xl" fontWeight="700" mb="4px">
        {t("Content Overview")}
      </Text>
      <Text fontSize="xs" color="gray.500" mb="14px">
        {t("All-time totals, not affected by the date range above")}
      </Text>

      {byStatusEntries.length > 0 && (
        <Wrap mb={4}>
          {byStatusEntries.map(([status, count]) => (
            <WrapItem key={status}>
              <Badge px={2} py={1} borderRadius="md" bg={GOLD_LIGHT} color={GOLD_TEXT}>
                {status}: {count}
              </Badge>
            </WrapItem>
          ))}
        </Wrap>
      )}

      <SimpleGrid columns={{ base: 1, md: 2 }} gap={6}>
        <Box>
          <Text fontWeight="600" fontSize="sm" mb={2}>{t("By Category")}</Text>
          <RankedList items={overview?.byCategory} renderLabel={(item) => item.name || item.slug} t={t} />
        </Box>
        <Box>
          <Text fontWeight="600" fontSize="sm" mb={2}>{t("By Author")}</Text>
          <RankedList items={overview?.byAuthor} renderLabel={(item) => item.name} t={t} />
        </Box>
      </SimpleGrid>
    </Card>
  );
}
